import React from "react";
import FormInput, { NumberFormInput } from "../widgets/FormInput";
import TextArea from "../widgets/TextArea";
import { Formik } from "formik";
import * as Yup from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addProduct } from "src/api/inventory";
import { showSuccessToast } from "src/lib/utils";
import { Button } from "../ui/button";
import { usePortalContext } from "src/context/PortalContext";
import CustomSelect from "../CustomSelect";
import { useDashboardContext } from "src/context/DashboardContext";

const validationSchema = Yup.object().shape({
	name: Yup.string().required("This field is required"),
	sku: Yup.string().required("This field is required"),
	cost_price: Yup.string().required("This field is required"),
	price: Yup.string().required("This field is required"),
	qty: Yup.number().required("This field is required"),
	store_or_warehouse_id: Yup.string().required("This field is required"),
	description: Yup.string(),
});

const AddProductModal = ({ onSubmit }) => {
	const closePortal = usePortalContext();
	const queryClient = useQueryClient();
	const { entities } = useDashboardContext();

	const initialValues = {
		name: "",
		sku: "",
		price: "",
		qty: 0,
		cost_price: "",
		description: "",
		store_or_warehouse_id: "",
		image: null,
	};

	const entityOptions = entities?.map(({ id, name }) => ({ label: name, value: String(id) }));

	const { mutate, isLoading } = useMutation(addProduct, {
		onSuccess: (data) => {
			showSuccessToast("Product added successfully");
			queryClient.invalidateQueries(["inventory"]);
			closePortal();
			onSubmit?.(data?.data);
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit, setFieldValue, errors }) => (
				<form onSubmit={handleSubmit} className="grid">
					<FormInput
						className="formItem !tw-w-full"
						label="Name"
						name="name"
						type="text"
						placeholder="Name"
						value={values.name}
						error={errors.name}
						onChange={handleChange}
					/>

					<NumberFormInput
						className="formItem !tw-w-full"
						label="Quantity"
						name="qty"
						type="text"
						placeholder="Quantity"
						value={values.qty}
						error={errors.qty}
						onChange={(value) => setFieldValue("qty", value)}
					/>

					<FormInput
						className="formItem !tw-w-full"
						label="SKU"
						name="sku"
						type="text"
						placeholder="SKU"
						value={values.sku}
						error={errors.sku}
						onChange={handleChange}
					/>

					<NumberFormInput
						className="formItem !tw-w-full"
						label="Cost Price"
						name="cost_price"
						type="text"
						placeholder="Cost Price"
						value={values.cost_price}
						error={errors.cost_price}
						onChange={(value) => setFieldValue("cost_price", value)}
					/>

					<NumberFormInput
						className="formItem !tw-w-full"
						label="Selling Price"
						name="price"
						type="text"
						placeholder="Selling Price"
						value={values.price}
						error={errors.price}
						onChange={(value) => setFieldValue("price", value)}
					/>

					<CustomSelect
						name="store_or_warehouse_id"
						className="tw-mb-4"
						label="Select Warehouse/Storefront"
						placeholder="Select Warehouse/Storefront"
						value={values.store_or_warehouse_id}
						error={errors.store_or_warehouse_id}
						onChange={(value) => setFieldValue("store_or_warehouse_id", value)}
						options={entityOptions}
					/>

					<TextArea label="Description" name="description" placeholder="Description" value={values.description} onChange={handleChange} />

					<FormInput
						className="formItem !tw-w-full"
						label="Image"
						name="image"
						type="file"
						onChange={(event) => setFieldValue("image", event.currentTarget.files[0])}
					/>

					<Button variant="secondary" className="tw-w-full" disabled={isLoading}>
						{isLoading ? "Submitting..." : "Create Product"}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default AddProductModal;
